import { App, Button, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';

const saveBlobCrossPlatform = (blob: Blob, filename: string) => {
  const w = window as any;
  if (w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function') {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        w.AndroidInterface.saveBlobFile(base64, filename);
      } catch {}
    };
    reader.readAsDataURL(blob);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const sanitizeFilename = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildPdfFilename = (record: any, fallbackId: string) => {
  const companyName = record?.company?.name ?? '';
  const workPlaceName = record?.workPlace?.name ?? '';
  const fileNumber = record?.number ?? '';
  const base =
    !companyName || !workPlaceName || !fileNumber ? fallbackId : `${companyName} ${workPlaceName} ${fileNumber}`;
  return `${sanitizeFilename(base)}.pdf`;
};

const InterventionFilePreview: React.FC = () => {
  const { id } = useParams();
  const { message } = App.useApp();
  const [record, setRecord] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let nextBlobUrl: string | null = null;

    if (!id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    Promise.all([
      InterventionFileService.search({
        ids: [id],
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
      }),
      InterventionFileService.print(id),
    ])
      .then(async ([data, response]) => {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const blob = await response.blob();
        nextBlobUrl = window.URL.createObjectURL(blob);

        if (!isMounted) {
          return;
        }

        setRecord(data?.response?.[0] ?? null);
        setPdfBlobUrl((previousUrl) => {
          if (previousUrl) {
            window.URL.revokeObjectURL(previousUrl);
          }
          return nextBlobUrl;
        });
        nextBlobUrl = null;
      })
      .catch((error: any) => {
        console.error('Error loading intervention file preview:', error);
        message.error('Nu am putut incarca previzualizarea fisei de interventie.');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      if (nextBlobUrl) {
        window.URL.revokeObjectURL(nextBlobUrl);
      }
    };
  }, [id, message]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const handleDownload = async () => {
    if (!id) return;
    try {
      setIsDownloading(true);
      const response = await InterventionFileService.print(id);
      const blob = await response.blob();
      const filename = buildPdfFilename(record, id);
      saveBlobCrossPlatform(blob, filename);
    } catch (error) {
      console.error('Error downloading intervention file:', error);
      message.error('A aparut o eroare la descarcarea fisei de interventie.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: '#3C3C3C',
          color: '#fff',
        }}
      >
        <div>Intervention File</div>
        <Button type='primary' onClick={handleDownload} loading={isDownloading}>
          Download
        </Button>
      </div>
      <div style={{ flex: 1, background: '#f0f2f5' }}>
        {isLoading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : (
          <iframe title='Intervention File PDF' src={pdfBlobUrl ?? ''} style={{ width: '100%', height: '100%', border: 0 }} />
        )}
      </div>
    </div>
  );
};

export default InterventionFilePreview;
